import {Button, ButtonGroup, DialogFooter} from 'platform/components';
import {Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';
import {RadioCard} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

export type AddCreatedArticleAction = 'non-binding' | 'request';

const RADIO_GROUP_NAME = 'addCreatedArticleAction';

export interface AddCreatedArticleDialogProps extends RequiredTestIdProps {
  onConfirm: (warehouseAction: AddCreatedArticleAction) => Promise<void> | void;
  onClose: VoidFunction;
}

export function AddCreatedArticleDialog(props: AddCreatedArticleDialogProps) {
  const [selectedAction, setSelectedAction] = useState<AddCreatedArticleAction | null>(null);
  const [isSubmitting, startSubmitting, stopSubmitting] = useBoolean();

  const handleConfirm = () => {
    if (isNil(selectedAction) || isSubmitting) {
      return;
    }

    startSubmitting();

    return Promise.resolve(props.onConfirm(selectedAction)).finally(stopSubmitting);
  };

  return (
    <>
      <VStack spacing={4} data-testid={props['data-testid']}>
        <Text>{i18n.t('entity.warehouse.labels.howToAddArticleToBasket')}</Text>
        <VStack spacing={2} role="radiogroup">
          <RadioCard
            name={RADIO_GROUP_NAME}
            isSelected={selectedAction === 'non-binding'}
            title={i18n.t('entity.warehouse.labels.nonBinding')}
            description={i18n.t('entity.warehouse.labels.addWithoutWarehouseRequest')}
            onClick={() => setSelectedAction('non-binding')}
            data-testid={suffixTestId('option-nonBinding', props)}
          />
          <RadioCard
            name={RADIO_GROUP_NAME}
            isSelected={selectedAction === 'request'}
            title={i18n.t('entity.addMaterial.actions.addMaterialRequest')}
            description={i18n.t('entity.warehouse.labels.createWarehouseRequestForArticle')}
            onClick={() => setSelectedAction('request')}
            data-testid={suffixTestId('option-request', props)}
          />
        </VStack>
      </VStack>
      <DialogFooter>
        <ButtonGroup align="right">
          <Button
            variant="secondary"
            title={i18n.t('general.actions.cancel')}
            onClick={props.onClose}
            data-testid={suffixTestId('cancel', props)}
          />
          <Button
            variant="primary"
            title={i18n.t('general.actions.addToBasket')}
            onClick={handleConfirm}
            isDisabled={isNil(selectedAction) || isSubmitting}
            isLoading={isSubmitting}
            data-testid={suffixTestId('addToBasket', props)}
          />
        </ButtonGroup>
      </DialogFooter>
    </>
  );
}
